/**
 * Smart List query parser.
 *
 * Grammar (recursive descent):
 *
 *   query   := or
 *   or      := and ( OR and )*
 *   and     := not ( AND not )*
 *   not     := NOT not | atom
 *   atom    := '(' or ')' | KEY ':' VALUE | KEY
 *
 * Precedence (tightest to loosest): NOT > AND > OR.
 * Booleans (AND, OR, NOT) are matched case-insensitively.
 * Keys are lowercased; values are preserved case-sensitively because they
 * may be tag names, project names, etc.
 */

export type Query = AndQuery | OrQuery | NotQuery | AtomQuery;

export interface AtomQuery {
  type: 'atom';
  key: string;
  value: string | undefined;
}

export interface AndQuery {
  type: 'and';
  left: Query;
  right: Query;
}

export interface OrQuery {
  type: 'or';
  left: Query;
  right: Query;
}

export interface NotQuery {
  type: 'not';
  child: Query;
}

export interface ParseQueryError {
  error: string;
}

export type ParseQueryResult = Query | ParseQueryError;

// ─── Lexer ────────────────────────────────────────────────────────────────

type Token =
  | { type: 'lparen' }
  | { type: 'rparen' }
  | { type: 'and' }
  | { type: 'or' }
  | { type: 'not' }
  | { type: 'word'; value: string };

const KEYWORDS: Record<string, Token['type']> = {
  and: 'and',
  or: 'or',
  not: 'not',
};

function lex(input: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  const n = input.length;

  while (i < n) {
    const ch = input[i] as string;
    if (ch === ' ' || ch === '\t' || ch === '\n') {
      i++;
      continue;
    }
    if (ch === '(') {
      tokens.push({ type: 'lparen' });
      i++;
      continue;
    }
    if (ch === ')') {
      tokens.push({ type: 'rparen' });
      i++;
      continue;
    }
    // Read a "word" — anything up to whitespace or paren, except quoted
    // fragments may contain spaces (e.g. `tag:"deep work"`).
    let j = i;
    let quoted = false;
    while (j < n) {
      const c = input[j] as string;
      if (c === '"') {
        quoted = !quoted;
        j++;
        continue;
      }
      if (!quoted && (c === ' ' || c === '\t' || c === '\n' || c === '(' || c === ')')) break;
      j++;
    }
    const word = input.slice(i, j);
    const lower = word.toLowerCase();
    if (KEYWORDS[lower]) {
      tokens.push({ type: KEYWORDS[lower] } as Token);
    } else {
      tokens.push({ type: 'word', value: word });
    }
    i = j;
  }

  return tokens;
}

// ─── Parser ──────────────────────────────────────────────────────────────

class Parser {
  private pos = 0;
  constructor(private readonly tokens: Token[]) {}

  parse(): Query {
    const result = this.parseOr();
    if (this.pos < this.tokens.length) {
      throw new Error(`unexpected token at position ${this.pos}`);
    }
    return result;
  }

  private peek(): Token | undefined {
    return this.tokens[this.pos];
  }

  private consume(): Token {
    const token = this.tokens[this.pos];
    if (!token) throw new Error('unexpected end of input');
    this.pos++;
    return token;
  }

  private parseOr(): Query {
    let left = this.parseAnd();
    while (this.peek()?.type === 'or') {
      this.consume();
      const right = this.parseAnd();
      left = { type: 'or', left, right };
    }
    return left;
  }

  private parseAnd(): Query {
    let left = this.parseNot();
    while (this.peek()?.type === 'and') {
      this.consume();
      const right = this.parseNot();
      left = { type: 'and', left, right };
    }
    return left;
  }

  private parseNot(): Query {
    if (this.peek()?.type === 'not') {
      this.consume();
      const child = this.parseNot();
      return { type: 'not', child };
    }
    return this.parseAtom();
  }

  private parseAtom(): Query {
    const token = this.peek();
    if (!token) throw new Error('unexpected end of input');

    if (token.type === 'lparen') {
      this.consume();
      const inner = this.parseOr();
      const close = this.peek();
      if (!close || close.type !== 'rparen') {
        throw new Error('unmatched (');
      }
      this.consume();
      return inner;
    }

    if (token.type === 'word') {
      this.consume();
      const colon = token.value.indexOf(':');
      if (colon === -1) {
        return { type: 'atom', key: token.value.toLowerCase(), value: undefined };
      }
      const key = token.value.slice(0, colon).toLowerCase();
      const value = stripQuotes(token.value.slice(colon + 1));
      return { type: 'atom', key, value };
    }

    throw new Error(`unexpected token ${token.type}`);
  }
}

function stripQuotes(value: string): string {
  return value.length >= 2 && value.startsWith('"') && value.endsWith('"')
    ? value.slice(1, -1)
    : value;
}

export function parseQuery(input: string): ParseQueryResult {
  const trimmed = input.trim();
  if (!trimmed) return { error: 'empty' };
  try {
    const tokens = lex(trimmed);
    if (tokens.length === 0) return { error: 'empty' };
    return new Parser(tokens).parse();
  } catch (err) {
    return { error: err instanceof Error ? err.message : 'parse error' };
  }
}
